// src/pages/TeamQueue.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import {
  Search,
  Filter,
  Clock,
  User as UserIcon,
  Mail,
  Phone,
  Calendar,
  Eye,
  Edit,
  CheckCircle2,
  AlertCircle,
  FileText,
  Loader2,
  LogOut,
  Grid,
  List
} from 'lucide-react';
import { format } from 'date-fns';

export default function TeamQueue() {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      checkUserRole();
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchTerm, statusFilter]);

  const checkUserRole = async () => {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData.role && userData.role !== 'client') {
          setUserRole(userData.role);
          await fetchSubmissions();
        } else {
          navigate('/dashboard');
        }
      } else {
        console.error('User document not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      navigate('/dashboard');
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoadingSubmissions(true);
      const snapshot = await getDocs(collection(db, 'submissions'));
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      data.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      
      setSubmissions(data);
      setFilteredSubmissions(data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];
    
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }
    
    setFilteredSubmissions(filtered);
  };

  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      const submissionRef = doc(db, 'submissions', submissionId);
      await updateDoc(submissionRef, { status: newStatus });
      
      setSubmissions(prev => prev.map(sub => 
        sub.id === submissionId ? { ...sub, status: newStatus } : sub
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'submitted': 'bg-blue-100 text-blue-800',
      'in-design': 'bg-purple-100 text-purple-800',
      'draft-review': 'bg-orange-100 text-orange-800',
      'published': 'bg-green-100 text-green-800'
    };
    return badges[status] || badges['submitted'];
  };

  if (loading || loadingSubmissions || !userRole) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div>
                <span className="text-xl font-semibold text-neutral-900">Stories for Memories</span>
                <span className="ml-2 text-sm text-neutral-500">Team Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-neutral-600">{user?.email}</span>
              <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                {userRole}
              </span>
              <button onClick={handleLogout} className="p-2 hover:bg-neutral-100 rounded-lg">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2">Submission Queue</h1>
          <p className="text-neutral-600">{filteredSubmissions.length} submissions</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="in-design">In Design</option>
                <option value="draft-review">Draft Review</option>
                <option value="published">Published</option>
              </select>
              <button
                onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
                className="p-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                {viewMode === 'table' ? <Grid className="w-5 h-5" /> : <List className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {filteredSubmissions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
            <FileText className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">No submissions found</h3>
            <p className="text-neutral-600">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-neutral-900">{submission.fullName}</p>
                        <p className="text-sm text-neutral-500">{submission.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600">
                      {submission.createdAt ? format(new Date(submission.createdAt), 'MMM d, yyyy') : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={submission.status || 'submitted'}
                        onChange={(e) => handleStatusUpdate(submission.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(submission.status)}`}
                      >
                        <option value="submitted">Submitted</option>
                        <option value="in-design">In Design</option>
                        <option value="draft-review">Draft Review</option>
                        <option value="published">Published</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => window.open(submission.headshotURL, '_blank')}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
